import { useEffect, useRef, useState } from 'react';

export default function PhotoThumbnail({ src, alt }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [retryKey, setRetryKey] = useState(0);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    setStatus('loading');
    setRetryKey((prev) => prev + 1);
    retryCountRef.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, [src]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const handleError = () => {
    setStatus('loading');
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    // Exponential backoff: base 1s, doubling each retry (1s, 2s, 4s, 8s...), capped at 16s with slight jitter
    const delay =
      Math.min(1000 * Math.pow(2, retryCountRef.current), 16000) +
      Math.random() * 200;
    retryCountRef.current += 1;

    retryTimeoutRef.current = setTimeout(() => {
      setRetryKey((prev) => prev + 1);
    }, delay);
  };

  const handleLoad = () => {
    setStatus('loaded');
    retryCountRef.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  };

  const currentSrc = retryKey > 0 ? `${src}?t=${retryKey}` : src;

  return (
    <div className="w-full h-full relative flex items-center justify-center bg-rose-50/60">
      {status !== 'loaded' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-rose-300">
          <svg
            className="animate-spin h-7 w-7 text-rose-400 mb-1.5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span className="text-xs text-rose-400/80 font-medium select-none">
            Processing...
          </span>
        </div>
      )}
      <img
        key={`${src}-${retryKey}`}
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-300 hover:scale-105 ${
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
